import {
  DestinationCityColumn,
  DestinationNameColumn,
  DestinationPostalCodeColumn,
  DestinationStateColumn,
  DestinationStreetColumn,
  OriginCityColumn,
  OriginNameColumn,
  OriginPostalCodeColumn,
  OriginStateColumn,
  OriginStreetColumn,
} from '@/pages/OrderManagement/components/Grid/Columns/DefaultColumnDefs'
import {
  CheckSquareFilled,
  ClockCircleOutlined,
  DownOutlined,
  FileExclamationFilled,
  RightOutlined,
  WarningFilled,
} from '@ant-design/icons'
import errorsBypassedIcon from '@assets/CustomIcons/error_approved.svg'
import UserIcon from '@assets/CustomIcons/user_edited.svg'
import { defaultDateDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { OptimizingIcon } from '@components/_SiteWide/OrderDisplay/OptimizingIcon'
import { OrderModalButton } from '@components/_SiteWide/OrderDisplay/OrderModalButton'
import { OrderStores } from '@components/_SiteWide/OrderDisplay/OrderStores'
import { OrderTerminals } from '@components/_SiteWide/OrderDisplay/OrderTerminals'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { getDisplayTimezoneString } from '@utils/general'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Col, Popover, Row, Tooltip } from 'antd'
import moment from 'moment/moment'
import { useMemo, useState } from 'react'
import { EbolStatusTag } from './EbolMatching/EbolStatusTag'

export const getColumnDefs = (showModal, backOfficeErp, canViewOrder, isCompact, lockedOrders, canViewEbol = false) => {
  return [
    {
      cellRenderer: (params) => <OrderMovementsExpandCell params={params} lockedOrders={lockedOrders} />,
      cellRendererParams: {},
      field: 'id',
      valueFormatter: () => '',
      headerName: '',
      maxWidth: 50,
      filter: false,
      ...(isCompact && { pinned: 'left' }),
    },
    canViewEbol
      ? {
          headerName: 'E-BOL Status',
          field: 'ebol_status',
          pinned: 'left',
          maxWidth: 150,
          cellRenderer: ({ data, node, api }) => (
            <EbolStatusTag
              ebol_status={data.ebol_status}
              orderId={data.id}
              onClick={() => api.setRowNodeExpanded(node, true)}
            />
          ),
        }
      : undefined,
    {
      width: 100,
      flex: 0,
      headerName: 'Validations',
      headerTooltip: 'Validations',
      field: 'movement_validation_issues',
      cellRenderer: ({ data }) => {
        if (data?.validation_bypass) {
          return (
            <Tooltip title='Errors Bypassed'>
              <img src={errorsBypassedIcon} />{' '}
            </Tooltip>
          )
        }
        if (data?.movement_validation_issues?.length > 0 && !data?.validation_bypass) {
          return (
            <Tooltip title='Click To View Errors'>
              <GraviButton icon={<WarningFilled />} error />
            </Tooltip>
          )
        }
        return null
      },
      valueGetter: ({ data }) => {
        if (data?.validation_bypass) {
          return 'Bypassed'
        }
        if (data?.movement_validation_issues?.length > 0 && !data?.validation_bypass) {
          return 'Has Errors'
        }
        return 'Valid'
      },
    },
    {
      width: 100,
      flex: 0,
      headerName: 'Export',
      headerTooltip: 'Export',
      field: 'backoffice_erp.status',
      comparator: (a, b) => {
        // For orders not yet complete, (`None` in the filter dropdown), the status field doesn't exist, which breaks sorting.
        // So we're modifying the status on the fly here in the sort function and replacing any null instances with 'none'
        const exists = (val) => typeof val !== 'undefined' && val !== null

        let status_a = a?.backoffice_erp?.status
        let status_b = b?.backoffice_erp?.status

        if (!exists(status_a)) status_a = 'none'
        if (!exists(status_b)) status_b = 'none'

        return status_a > status_b ? 1 : -1
      },
      cellRenderer: ({ data }) => {
        return <ExportStatusCell backoffice_erp={data?.backoffice_erp} />
      },
      hide: !backOfficeErp,
    },
    {
      minWidth: 100,
      headerName: '',
      field: 'show_order_modal',
      hide: !canViewOrder,
      cellRenderer: ({ data }) => {
        const lockedOrder = lockedOrders.find((o) => o.order_id === data.id)
        return (
          <Horizontal verticalCenter justifyContent='space-between'>
            <Vertical flex={1}>
              <OrderModalButton orderId={data.id} showModal={showModal} />
            </Vertical>
            <Vertical flex={1}>{lockedOrder && <OptimizingIcon lockedOrder={lockedOrder} />}</Vertical>
          </Horizontal>
        )
      },
    },
    {
      headerName: 'Order No.',
      field: 'number',
      cellRenderer: ({ value, data }) => {
        const isError = !data?.validation_bypass && data?.movement_validation_issues?.length > 0
        return (
          <div className='vertical-flex-center'>
            <Texto category='h6' weight={isError && 'bold'} appearance={isError && 'error'}>
              {value}
            </Texto>
          </div>
        )
      },
      filter: true,
    },
    {
      headerName: 'Purchase Order',
      field: 'po',
      cellRenderer: ({ value }) => <Texto weight='bold'>{value}</Texto>,
      filter: true,
      initialHide: true,
    },
    {
      headerName: 'Destinations',
      autoHeight: true,
      field: 'drop',
      valueGetter: (params) => params?.data?.drops?.map((drop) => drop.store_name),
      cellRenderer: ({ data }) => <OrderStores order={data} />,
      filter: true,
    },
    DestinationNameColumn(),
    DestinationStreetColumn(),
    DestinationCityColumn(),
    DestinationStateColumn(),
    DestinationPostalCodeColumn(),
    {
      headerName: 'Origins',
      autoHeight: true,
      field: 'load',
      valueGetter: (params) => params?.data?.loads?.map((load) => load.location_name),
      cellRenderer: ({ data }) => <OrderTerminals order={data} showShortName={false} />,
      filter: true,
    },
    OriginNameColumn(),
    OriginStreetColumn(),
    OriginCityColumn(),
    OriginStateColumn(),
    OriginPostalCodeColumn(),
    {
      headerName: 'Hauled By',
      field: 'hauled_by',
      cellRenderer: ({ value }) => <Texto weight='bold'>{value}</Texto>,
      filter: true,
    },
    {
      headerName: 'Attendant',
      field: 'attendant_note',
      valueGetter: (params) => params?.data?.attendant_note?.updated_by,
      initialHide: true,
    },
    {
      headerName: 'Creation Date',
      field: 'creation_date',
      ...defaultDateDef('creation_date'),
    },
    {
      headerName: 'Updated',
      field: 'movement_last_update',
      ...defaultDateDef('movement_last_update', undefined, true),
      cellRenderer: (params) => {
        const created = params.data.creation_date
        const updated = params.data.movement_last_update
        return (
          <Row className='order-stat-tag-container' style={{ flexWrap: 'nowrap' }}>
            <Col className='vertical-flex-center'>
              <img alt='user icon' src={UserIcon} style={{ width: 24, height: 24 }} />
            </Col>
            <Col flex={1} className=' pl-3 pr-4  vertical-flex-center h-100'>
              <div style={{ fontWeight: updated ? 'bold' : 'normal' }}>
                {formatters.SHORT_DATE_TIME(moment(updated || created))}
              </div>
            </Col>
          </Row>
        )
      },
      minWidth: getDisplayTimezoneString() ? 155 : 134,
    },
    {
      headerName: 'BOL Date',
      field: 'bol_date',
      ...defaultDateDef('bol_date'),
    },
    {
      headerName: 'Delivery Date',
      field: 'delivery_date',
      ...defaultDateDef('delivery_date', undefined, false, null),
    },
    {
      headerName: 'Delivery From',
      field: 'delivery_window_from',
      ...defaultDateDef('delivery_window_from', undefined),
      initialHide: true,
    },
    {
      headerName: 'Delivery To',
      field: 'delivery_window_to',
      ...defaultDateDef('delivery_window_to', undefined),
      initialHide: true,
    },
    {
      width: 150,
      flex: 0,
      headerName: 'Export Date',
      headerTooltip: 'Export Date',
      field: 'backoffice_erp',
      valueGetter: ({ data }) => data?.backoffice_erp?.time_sent,
      ...defaultDateDef('backoffice_erp', formatters.DATE_TIME, true, 'time_sent'),
      hide: !backOfficeErp,
    },
    {
      headerName: 'Tractor Number',
      field: 'tractor_number',
    },
    {
      headerName: 'Trailer Number',
      field: 'trailer_number',
    },
  ].filter((x) => x !== undefined)
}

function ExportStatusCell({ backoffice_erp }) {
  if (backoffice_erp?.status === 'errors') {
    return (
      <Popover
        content={
          <>
            <Texto weight={600}>Errors:</Texto>
            {backoffice_erp.errors.map((error, i) => (
              <Texto key={i} appearance='error' className='my-2'>
                {error}
              </Texto>
            ))}
          </>
        }
        title='Export Errors'
      >
        <GraviButton icon={<FileExclamationFilled />} error />
      </Popover>
    )
  }
  if (backoffice_erp?.status === 'sent') {
    return (
      <Tooltip title='Export Complete'>
        <div>
          <Texto category='h3' appearance='success'>
            <CheckSquareFilled />
          </Texto>
        </div>
      </Tooltip>
    )
  }
  if (backoffice_erp?.status === 'pending') {
    return (
      <Tooltip title='Pending Export'>
        <div>
          <Texto category='h3' appearance='medium'>
            <ClockCircleOutlined />
          </Texto>
        </div>
      </Tooltip>
    )
  }
  return null
}

export function OrderMovementsExpandCell({ params, lockedOrders }) {
  const [expanded, setExpanded] = useState(params.node?.expanded)

  const icon = useMemo(() => {
    return params.node?.expanded ? <DownOutlined /> : <RightOutlined />
  }, [params.node.expanded])

  if (lockedOrders?.some((o) => o.order_id === params.data.id)) {
    params.node.setExpanded(false)
    return ' '
  }

  return (
    <Horizontal alignItems='center'>
      <GraviButton
        //buttonText={<Texto>{icon}</Texto>}
        icon={icon}
        appearance='link'
        size='small'
        onClick={() => {
          params.api.setRowNodeExpanded(params.node, !expanded)
          setExpanded(!expanded)
        }}
        className='movements-expand-button'
      />
    </Horizontal>
  )
}
